import type { WalletAsset } from "@/interfaces/asset";
import { LedgerAccountType } from "@/interfaces/account";

/**
 * Safely converts any value to a number.
 */
export const safeNumber = (value: unknown): number => {
  const n = Number(value);

  return Number.isFinite(n) ? n : 0;
};

/**
 * Returns the available spendable balance for an asset.
 */
// export const getAvailableBalance = (asset?: WalletAsset): number => {
//   if (!asset?.accounts?.length) {
//     return 0;
//   }

//   const accounts = new Map(
//     asset.accounts.map((account) => [
//       account.type,
//       safeNumber(account.balance),
//     ]),
//   );

//   return (
//     accounts.get(LedgerAccountType.AVAILABLE) ??
//     accounts.get(LedgerAccountType.TREASURY) ??
//     accounts.get(LedgerAccountType.FEE) ??
//     0
//   );
// };

export const getAvailableBalance = (asset?: WalletAsset) => {
  return getSpendableBalance(asset);
};

export const getSpendableBalance = (asset?: WalletAsset) => {
  if (!asset) return 0;

  const available = asset.accounts?.find(
    (a) => a.type === LedgerAccountType.AVAILABLE,
  );

  if (available) {
    return safeNumber(available.balance);
  }

  const treasury = asset.accounts?.find(
    (a) => (a.type as LedgerAccountType) === LedgerAccountType.TREASURY,
  );

  if (treasury) {
    return safeNumber(treasury.balance);
  }

  const fee = asset.accounts?.find(
    (a) => (a.type as LedgerAccountType) === LedgerAccountType.FEE,
  );

  if (fee) {
    return safeNumber(fee.balance);
  }

  return safeNumber(asset.available);
};

/**
 * Filters wallet assets by search query.
 */
export const filterAssets = (
  assets: WalletAsset[],
  query: string,
): WalletAsset[] => {
  const search = query?.trim().toLowerCase();

  if (!search) {
    return [...assets];
  }

  return assets.filter((asset) =>
    [
      asset.token?.name,
      asset.token?.symbol,
      asset.currency,
      asset.token?.blockchainNetwork?.name,
    ]
      .filter(Boolean)
      .some((value) => value!.toLowerCase().includes(search)),
  );
};

/**
 * Sort assets by:
 *
 * 1. Highest available balance
 * 2. Token name
 */
export const sortAssets = (assets: WalletAsset[]): WalletAsset[] => {
  return [...assets].sort((a, b) => {
    const balanceDiff = getAvailableBalance(b) - getAvailableBalance(a);

    if (balanceDiff !== 0) {
      return balanceDiff;
    }

    return (a.token?.name ?? "").localeCompare(b.token?.name ?? "");
  });
};

/**
 * Filters then sorts.
 */
export const searchAssets = (
  assets: WalletAsset[],
  query: string,
): WalletAsset[] => {
  return sortAssets(filterAssets(assets, query));
};
